// ...existing imports...
import type { NoticeWithDetails } from "../../gedoba/notices/queries";
import React, { useState, useMemo } from 'react';
import { useQuery } from 'wasp/client/operations';
import { getLocationNotices } from 'wasp/client/operations';
import { withSectionProtection } from '../../client/components/ProtectedSection';
import { useAuth } from 'wasp/client/auth';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Plus, 
  Bell, 
  AlertTriangle, 
  Info, 
  Calendar,
  User,
  MapPin,
  ExternalLink,
  Edit3,
  Trash2,
  Eye,
  Pin,
  Archive,
  Grid3X3,
  List,
  Table
} from 'lucide-react';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { 
  EnhancedPageLayout,
  EnhancedPageHeader, 
  EnhancedFilterSection, 
  ResponsiveViewSwitcher, 
  EnhancedActionButton, 
  EnhancedIconButton, 
  enhancedPageLayoutStyles,
  useResponsiveLayout
} from '../../components/ui/enhanced-page-layout';
import { useFixedPageStack } from '../../client/hooks/useFixedPageStack';
import {
  NOTICE_PRIORITY_FILTER_OPTIONS,
  PRIORITY_SORT_ORDER,
  getPriorityColorClass,
} from './noticeConstants';

type NoticeCategory = 'announcement' | 'warning' | 'info' | 'maintenance' | 'event' | 'general' | 'all';
type NoticePriorityFilter = 'low' | 'medium' | 'high' | 'very_high' | 'all';

const NOTICE_CATEGORIES = [
  { value: 'all', label: 'All Categories', icon: Filter },
  { value: 'announcement', label: 'Announcement', icon: Bell },
  { value: 'warning', label: 'Warning', icon: AlertTriangle },
  { value: 'info', label: 'Information', icon: Info },
  { value: 'maintenance', label: 'Maintenance', icon: null },
  { value: 'event', label: 'Event', icon: Calendar },
  { value: 'general', label: 'General', icon: null }
];

function NoticeBoardPage() {
  const { data: user } = useAuth();
  const { selectedLocation, getLocationIdsForQuery } = useLocationContext();
  const locationIdsForQuery = getLocationIdsForQuery();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const routerLocation = useLocation();
  const locSlug = resolveLocationSlug(routerLocation.pathname, params.slug, selectedLocation);
  
  // State for filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<NoticeCategory>('all');
  const [selectedPriority, setSelectedPriority] = useState<NoticePriorityFilter>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'priority' | 'title'>('newest');
  const [showFilters, setShowFilters] = useState(false);
  // Use responsive layout hook
  const { viewMode, setViewMode, isMobile, shouldCollapseSidebar, hideViewSwitcher } = useResponsiveLayout('table');

  // Fetch notices using hub aggregation-aware query
  const {
    data: notices = [],
    isLoading,
    error
  } = useQuery(getLocationNotices, {
    locationIds: locationIdsForQuery,
    filters: {
      category: selectedCategory !== 'all' ? selectedCategory : undefined,
      priority: selectedPriority !== 'all' ? selectedPriority : undefined,
      searchTerm: searchTerm || undefined
    },
  }, {
    enabled: locationIdsForQuery.length > 0
  }) as { data: NoticeWithDetails[]; isLoading: boolean; error: any };

  // Filter and sort notices
  const filteredAndSortedNotices = useMemo(() => {
    let filtered: NoticeWithDetails[] = notices;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(notice => 
        notice.title.toLowerCase().includes(searchLower) ||
        notice.description?.toLowerCase().includes(searchLower) ||
        notice.contentBody?.toLowerCase().includes(searchLower) ||
        notice.category?.toLowerCase().includes(searchLower) ||
        notice.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'priority':
          const aPriority = PRIORITY_SORT_ORDER[a.metadata?.priority as string] || PRIORITY_SORT_ORDER['low'];
          const bPriority = PRIORITY_SORT_ORDER[b.metadata?.priority as string] || PRIORITY_SORT_ORDER['low'];
          return bPriority - aPriority;
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return sorted;
  }, [notices, searchTerm, selectedCategory, selectedPriority, sortBy]);

  const fixedStack = useFixedPageStack({
    secondaryKind:
      viewMode === 'table' && !isLoading && !error && notices.length > 0
        ? 'ref'
        : 'none',
    secondaryRefDeps: [viewMode, showFilters, notices.length, isLoading, error],
  });

  const getPriorityColor = (priority: string) => {
    if (priority === 'very_high') return 'text-red-800 bg-red-100 border-red-300';
    return getPriorityColorClass(priority);
  };

  const getCategoryIcon = (category: string) => {
    const categoryData = NOTICE_CATEGORIES.find(cat => cat.value === category);
    if (categoryData?.icon) {
      const IconComponent = categoryData.icon;
      return <IconComponent className="w-4 h-4" />;
    }
    return <Bell className="w-4 h-4" />;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  if (!selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Location Selected</h2>
            <p className="text-gray-600">Please select a location to view notices.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Removed header collapse functionality; header always visible

  return (
    <DashboardLayout 
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className={fixedStack.pageContainerClassName} style={fixedStack.pageContainerStyle}>
        {/* Fixed Header */}
        <div
          ref={fixedStack.pageHeaderRef}
          className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-white transition-all duration-300"
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Notice Board</h1>
                <p className="text-sm text-gray-600 mt-1">Explore notices and announcements at {selectedLocation.name}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                </button>
                {user && (
                  <EnhancedActionButton
                    onClick={() => navigate(locationSectionPath(locSlug, 'notices', 'add'))}
                    icon={Plus}
                    label={<span className="hidden sm:inline text-white">Post Notice</span>}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Table Header - Only for table view */}
        {!isLoading && !error && notices.length > 0 && viewMode === 'table' && (
          <div
            ref={fixedStack.secondaryStackRef}
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-sidebar-hover border-b border-border transition-all duration-300`}
          >
            <div className="px-6 py-3 space-y-3">
              {/* Column Headers */}
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
                <div className="col-span-4">NOTICE</div>
                <div className="col-span-2">CATEGORY</div>
                <div className="col-span-1">PRIORITY</div>
                <div className="col-span-2">AUTHOR</div>
                <div className="col-span-2">DATE</div>
                <div className="col-span-1">VIEWS</div>
              </div>
              {/* Column Filters */}
              {showFilters && <div className="grid grid-cols-12 gap-4">
                {/* Notice Filter */}
                <div className="col-span-4">
                  <input
                    type="text"
                    placeholder="Search notices..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {/* Category Filter */}
                <div className="col-span-2">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value as NoticeCategory)}
                    className="appearance-none w-full px-3 py-2 pr-6 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {NOTICE_CATEGORIES.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Priority Filter */}
                <div className="col-span-1">
                  <select
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value as NoticePriorityFilter)}
                    className="appearance-none w-full px-3 py-2 pr-6 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {NOTICE_PRIORITY_FILTER_OPTIONS.map(priority => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Author Filter - placeholder */}
                <div className="col-span-2">
                  <select
                    className="appearance-none w-full px-3 py-2 pr-6 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled
                  >
                    <option value="All">All</option>
                  </select>
                </div>
                {/* Date Filter - Sort selector */}
                <div className="col-span-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                    className="appearance-none w-full px-3 py-2 pr-6 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="priority">Priority</option>
                    <option value="title">Title A-Z</option>
                  </select>
                </div>
                {/* Views Filter - placeholder */}
                <div className="col-span-1">
                  <div className="w-full px-3 py-2"></div>
                </div>
              </div>}
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div
          className={fixedStack.scrollContainerClassName}
          style={fixedStack.scrollContainerStyle}
        >
          <div className="w-full">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading notices...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Notices</h3>
                <p className="text-gray-600">Please try again later.</p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredAndSortedNotices.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Notices Found</h3>
                <p className="text-gray-600">
                  {searchTerm || selectedCategory !== 'all' || selectedPriority !== 'all'
                    ? 'No notices match your current filters.'
                    : 'Be the first to post a notice for this location.'
                  }
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {!isLoading && !error && filteredAndSortedNotices.length > 0 && (
            <>
              {/* Cards View - Use enhanced layout with padding */}
              {viewMode === 'cards' && (
                <div className="px-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredAndSortedNotices.map((notice) => (
                      <div
                        key={notice.id}
                        className={`${enhancedPageLayoutStyles.card.container} ${
                          notice.isPinned ? 'border-blue-500 shadow-sm' : ''
                        } cursor-pointer hover:shadow-md transition-shadow`}
                        onClick={() => navigate(locationSectionPath(locSlug, 'notices', notice.id))}
                      >
                        <div className={enhancedPageLayoutStyles.card.content}>
                          <div className={enhancedPageLayoutStyles.card.header}>
                            <div className="flex items-center gap-2">
                              {getCategoryIcon(notice.category || 'general')}
                              <span className="text-sm font-medium text-gray-600 capitalize">
                                {notice.category || 'General'}
                              </span>
                              {notice.isPinned && (
                                <Pin className="w-4 h-4 text-blue-600" />
                              )}
                            </div>
                            
                            {notice.metadata?.priority && (
                              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(notice.metadata.priority)}`}>
                                {notice.metadata.priority.charAt(0).toUpperCase() + notice.metadata.priority.slice(1)}
                              </span>
                            )}
                          </div>
                          
                          <h3 className={enhancedPageLayoutStyles.card.title}>
                            {notice.title}
                          </h3>
                        
                          {notice.description && (
                            <p className={enhancedPageLayoutStyles.card.description}>
                              {notice.description}
                            </p>
                          )}

                          {/* Notice Content Preview */}
                          {notice.contentBody && (
                            <div className="px-4 py-3 border-b border-border">
                              <p className="text-gray-700 text-sm line-clamp-4">
                                {notice.contentBody}
                              </p>
                            </div>
                          )}

                          {/* Notice Images */}
                          {notice.images && notice.images.length > 0 && (
                            <div className="px-4 py-3 border-b border-border">
                              <div className="grid grid-cols-2 gap-2">
                                {notice.images.slice(0, 4).map((image, index) => (
                                  <img
                                    key={index}
                                    src={image}
                                    alt={`Notice image ${index + 1}`}
                                    className="w-full h-20 object-cover rounded-lg"
                                  />
                                ))}
                              </div>
                              {notice.images.length > 4 && (
                                <p className="text-xs text-gray-500 mt-2">
                                  +{notice.images.length - 4} more images
                                </p>
                              )}
                            </div>
                          )}

                          {/* Tags */}
                          {notice.tags && notice.tags.length > 0 && (
                            <div className="px-4 py-2 border-b border-border">
                              <div className="flex flex-wrap gap-1">
                                {notice.tags.slice(0, 3).map((tag, index) => (
                                  <span
                                    key={index}
                                    className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                                {notice.tags.length > 3 && (
                                  <span className="text-xs text-gray-500">
                                    +{notice.tags.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          <div className={enhancedPageLayoutStyles.card.footer}>
                            <div className={enhancedPageLayoutStyles.card.meta}>
                              <div className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                <span>{notice.author?.email || 'Anonymous'}</span>
                              </div>
                              <span>{formatDate(notice.createdAt)}</span>
                              <div className="flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                <span>{notice.viewCount || 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* List View - Use enhanced layout with padding */}
              {viewMode === 'list' && (
                <div className="p-6">
                  <div className="space-y-4">
                    {filteredAndSortedNotices.map((notice) => (
                      <div
                        key={notice.id}
                        className={`bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow ${
                          notice.isPinned ? 'border-blue-500 shadow-sm' : ''
                        } cursor-pointer hover:bg-hover transition-colors`}
                        onClick={() => navigate(locationSectionPath(locSlug, 'notices', notice.id))}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {getCategoryIcon(notice.category || 'general')}
                              <span className="text-sm font-medium text-gray-600 capitalize">
                                {notice.category || 'General'}
                              </span>
                              {notice.isPinned && (
                                <Pin className="w-4 h-4 text-blue-600" />
                              )}
                              {notice.metadata?.priority && (
                                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(notice.metadata.priority)}`}>
                                  {notice.metadata.priority.charAt(0).toUpperCase() + notice.metadata.priority.slice(1)}
                                </span>
                              )}
                            </div>
                            
                            <div>
                              <h3 className="font-semibold text-gray-900 text-xl mb-2">
                                {notice.title}
                              </h3>
                              
                              {notice.description && (
                                <p className="text-gray-600 mb-3 line-clamp-2">
                                  {notice.description}
                                </p>
                              )}

                              <div className="flex items-center gap-6 text-sm text-gray-500">
                                <div className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  <span>{notice.author?.email || 'Anonymous'}</span>
                                </div>
                                <span>{formatDate(notice.createdAt)}</span>
                                <div className="flex items-center gap-1">
                                  <Eye className="w-4 h-4" />
                                  <span>{notice.viewCount || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Table View */}
            {viewMode === 'table' && (
              <div className="w-full">
                {/* Table Body - No internal scroll */}
                <div className="divide-y divide-gray-200">
                  {filteredAndSortedNotices.map((notice) => (
                    <div 
                      key={notice.id} 
                      className="px-6 py-3 hover:bg-hover transition-colors cursor-pointer"
                      onClick={() => navigate(locationSectionPath(locSlug, 'notices', notice.id))}
                    >
                      <div className="grid grid-cols-12 gap-4 items-start">
                        <div className="col-span-4">
                          <div className="flex items-start">
                            {notice.isPinned && (
                              <Pin className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {notice.title}
                              </div>
                              {notice.description && (
                                <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                                  {notice.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="flex items-center">
                            {getCategoryIcon(notice.category || 'general')}
                            <span className="ml-2 text-sm text-gray-600 capitalize">
                              {notice.category || 'General'}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <span className="text-sm text-gray-600">
                            {notice.metadata?.priority ? 
                              notice.metadata.priority.charAt(0).toUpperCase() + notice.metadata.priority.slice(1) : 
                              'N/A'
                            }
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-gray-500">
                            {notice.author?.email || 'Anonymous'}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-gray-500">
                            {formatDate(notice.createdAt)}
                          </span>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            <span>{notice.viewCount || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
export default withSectionProtection(NoticeBoardPage, 'notices');



